import React from "react";
import "../PrivacyPolicy.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import { motion } from "framer-motion";

import {
    FaShieldAlt,
    FaLock,
    FaDatabase,
    FaCookieBite,
    FaUserShield,
    FaUserCheck,
    FaHistory,
    FaEnvelope,
    FaPhoneAlt,
    FaMapMarkerAlt
} from "react-icons/fa";

const policySections = [
    {
        icon: <FaDatabase />,
        title: "Information We Collect",
        content:
            "BlueLotus Infrasafety Projects Private Limited may collect personal information including your name, email address, phone number, company details, and any information you voluntarily provide through our contact forms, consultation requests, newsletters, or other communication channels. We may also collect technical information such as browser type, IP address, device information, operating system, and website usage analytics to improve our services and user experience."
    },
    {
        icon: <FaUserShield />,
        title: "How We Use Your Information",
        content:
            "The information we collect is used to respond to inquiries, provide cybersecurity services, improve our website, deliver requested information, communicate important updates, enhance customer support, and maintain the security of our systems. We never sell or rent your personal information to third parties."
    },
    {
        icon: <FaCookieBite />,
        title: "Cookies & Tracking Technologies",
        content:
            "Our website uses cookies and similar technologies to improve website functionality, analyze visitor behavior, remember user preferences, and enhance your browsing experience. You may disable cookies through your browser settings; however, certain website features may not function correctly."
    },
    {
        icon: <FaLock />,
        title: "Data Protection & Security",
        content:
            "Protecting your information is our highest priority. We implement industry-standard administrative, technical, and physical security measures including encryption, secure servers, access controls, monitoring systems, and cybersecurity best practices to protect your personal information against unauthorized access, alteration, disclosure, or destruction."
    },
    {
        icon: <FaUserCheck />,
        title: "Your Privacy Rights",
        content:
            "You have the right to request access to your personal information, correct inaccurate data, request deletion of your information where applicable, and opt out of receiving promotional communications. We will respond to legitimate privacy requests in accordance with applicable laws and regulations."
    }
];

const PrivacyPolicy = () => {
    return (
        <>
            <Header />

            <title>Privacy-Policy | BlueLotus Infrasafety Projects Private Limited</title>
            {/* HERO */}

            <section className="privacy-hero">

                <div className="container">

                    <motion.div
                        className="privacy-hero-content"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: .8 }}
                    >

                        <span>

                            Privacy & Data Protection

                        </span>

                        <h1>

                            Privacy Policy

                        </h1>

                        <p>

                            BlueLotus Infrasafety Projects Private Limited is committed
                            to protecting your privacy and safeguarding the personal
                            information you share with us. This Privacy Policy explains
                            how we collect, use, store, protect, and disclose your
                            information when you interact with our website and services.

                        </p>

                    </motion.div>

                </div>

            </section>

            {/* INTRO */}

            <section className="privacy-intro">

                <div className="container">

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: .8 }}
                    >

                        <div className="privacy-tag">

                            <FaShieldAlt />

                            Privacy Commitment

                        </div>

                        <h2>

                            Your Trust Is Important To Us

                        </h2>

                        <p>

                            As a cybersecurity company, protecting information is at
                            the core of everything we do. We are committed to maintaining
                            transparency regarding how personal information is collected,
                            processed, stored, and protected. Our privacy practices are
                            designed to support confidentiality, integrity, compliance,
                            and responsible data handling while providing secure digital
                            experiences for our customers and website visitors.

                        </p>

                    </motion.div>

                </div>

            </section>

            {/* POLICY SECTIONS */}

            <section className="policy-section">

                <div className="container">

                    {policySections.map((item, index) => (

                        <motion.div
                            className="policy-card"
                            key={index}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                duration: .5,
                                delay: index * .15
                            }}
                        >

                            <div className="policy-icon">

                                {item.icon}

                            </div>

                            <div className="policy-content">

                                <h3>

                                    {item.title}

                                </h3>

                                <p>

                                    {item.content}

                                </p>

                            </div>

                        </motion.div>

                    ))}

                </div>

            </section>

            {/* DATA RETENTION */}

            <section className="privacy-extra">

                <div className="container">

                    <motion.div
                        className="extra-card"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: .7 }}
                    >

                        <div className="extra-icon">

                            <FaHistory />

                        </div>

                        <div>

                            <h3>Data Retention</h3>

                            <p>

                                We retain personal information only for as long as it is
                                necessary to fulfill the purposes outlined in this Privacy
                                Policy, comply with applicable legal obligations, resolve
                                disputes, enforce agreements, and maintain the integrity
                                of our business operations. Once information is no longer
                                required, it is securely deleted or anonymized in accordance
                                with our internal data retention procedures.

                            </p>

                        </div>

                    </motion.div>

                    {/* THIRD PARTY */}

                    <motion.div
                        className="extra-card"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: .7, delay: .15 }}
                    >

                        <div className="extra-icon">

                            <FaShieldAlt />

                        </div>

                        <div>

                            <h3>Third-Party Services</h3>

                            <p>

                                Our website may include links to third-party websites,
                                services, or platforms for your convenience. These external
                                websites operate independently and maintain their own privacy
                                policies. BlueLotus Infrasafety Projects Private Limited is
                                not responsible for the privacy practices or content of any
                                third-party websites. We encourage users to review their
                                respective privacy policies before sharing personal information.

                            </p>

                        </div>

                    </motion.div>

                    {/* POLICY UPDATES */}

                    <motion.div
                        className="extra-card"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: .7, delay: .25 }}
                    >

                        <div className="extra-icon">

                            <FaShieldAlt />

                        </div>

                        <div>

                            <h3>Changes To This Privacy Policy</h3>

                            <p>

                                BlueLotus Infrasafety Projects Private Limited may update
                                this Privacy Policy periodically to reflect changes in our
                                services, legal obligations, security practices, or business
                                operations. Any updates will be published on this page with
                                the revised effective date. We encourage visitors to review
                                this Privacy Policy regularly to stay informed about how we
                                protect personal information.

                            </p>

                        </div>

                    </motion.div>

                </div>

            </section>

            {/* CONTACT */}

            {/* <section className="privacy-contact">

                <div className="container">

                    <motion.div
                        className="contact-card"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: .8 }}
                    >

                        <h2>

                            Contact Us

                        </h2>

                        <p>

                            If you have any questions regarding this Privacy Policy,
                            our privacy practices, or how your information is handled,
                            please feel free to contact our team.

                        </p>

                        <div className="contact-list">

                            <div className="contact-item">

                                <FaEnvelope />

                                <span>

                                    info@bluelotusinfrasafety.com

                                </span>

                            </div>

                            <div className="contact-item">

                                <FaPhoneAlt />

                                <span>

                                    +91 XXXXX XXXXX

                                </span>

                            </div>

                            <div className="contact-item">

                                <FaMapMarkerAlt />

                                <span>

                                    Gurugram, Haryana, India

                                </span>

                            </div>

                        </div>

                    </motion.div>

                </div>

            </section> */}

            <Footer />

        </>
    );
};

export default PrivacyPolicy;